import GestionArchivo from "../Archivo/GestionArchivo";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import estanqueService from "../../services/estanqueService";
import usuarioService from "../../services/usuarioService";
import { usePermisos } from "../../context/PermisosContext";
import { isSuperAdmin } from "../../services/authUtils";

// --- utils permisos ---
const toPermisosArray = (p) => {
  if (Array.isArray(p)) return p.map(String);
  if (typeof p === "string") {
    return p.split(",").map((x) => x.trim()).filter(Boolean);
  }
  return [];
};
const permisosToCsv = (arr) => toPermisosArray(arr).join(",");

// --- textarea autosize ---
const autosize = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

const AdministrarEstanques = () => {
  const { can, canEdit } = usePermisos();
  const esSuper = useMemo(() => !!isSuperAdmin(), []);
  const puedeVer = esSuper || !!can?.("ESTANQUES_VIEW");
  const puedeEditar = esSuper || !!canEdit?.("ESTANQUES_EDIT");

  const [estanques, setEstanques] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [errorGlobal, setErrorGlobal] = useState("");
  const [mensajePorEstanque, setMensajePorEstanque] = useState({});
  const [busqueda, setBusqueda] = useState("");
  const estanquesFiltrados = useMemo(() => {
    const q = busqueda.trim().toLocaleLowerCase("es");
    if (!q) return estanques;
    return estanques.filter((e) => [e.nombre, e.descripcion, e.estaActivo ? "activo" : "inactivo"]
      .some((v) => String(v ?? "").toLocaleLowerCase("es").includes(q)));
  }, [estanques, busqueda]);

  // control para evitar ráfagas de fetch
  const loadingEstanquesRef = useRef(false);
  const loadingUsuariosRef = useRef(false);
  const fetchedOnceRef = useRef(false);

  // edición por fila
  const [editingRows, setEditingRows] = useState({}); // { [id]: {nombre, descripcion, estaActivo, permisos(csv)} }

  const cargarEstanques = useCallback(async () => {
    if (!puedeVer || loadingEstanquesRef.current) return;
    loadingEstanquesRef.current = true;
    try {
      const datos = (await estanqueService.obtenerEstanques()) || [];
      setEstanques(
        datos.map((e) => ({
          ...e,
          permisos: permisosToCsv(e.permisos ?? ""),
        }))
      );
      setErrorGlobal("");
    } catch (err) {
      console.error(err);
      setErrorGlobal("Error al cargar estanques.");
    } finally {
      loadingEstanquesRef.current = false;
      fetchedOnceRef.current = true;
    }
  }, [puedeVer]);

  const cargarUsuarios = useCallback(async () => {
    if (loadingUsuariosRef.current) return;
    loadingUsuariosRef.current = true;
    try {
      const resp = esSuper
        ? await usuarioService.listarPermisosTemporales()
        : await usuarioService.obtenerUsuariosCreadosPorAdmin();
      const data = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      setUsuarios(data);
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
      setUsuarios([]);
    } finally {
      loadingUsuariosRef.current = false;
    }
  }, [esSuper]);

  // Cargar una sola vez cuando ya tengo permiso
  useEffect(() => {
    if (puedeVer) cargarEstanques();
  }, [puedeVer, cargarEstanques]);

  useEffect(() => {
    cargarUsuarios();
  }, [cargarUsuarios]);

  // Cuando doy click en “Editar”
  const startEdit = (estanque) => {
    setEditingRows((prev) => ({
      ...prev,
      [estanque.id]: {
        nombre: estanque.nombre ?? "",
        descripcion: estanque.descripcion ?? "",
        estaActivo: !!estanque.estaActivo,
        permisos: permisosToCsv(estanque.permisos ?? ""),
      },
    }));
  };

  const cancelEdit = (id) => {
    setEditingRows((prev) => {
      const cp = { ...prev };
      delete cp[id];
      return cp;
    });
  };

  const onEditChange = (id, campo, valor) => {
    setEditingRows((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [campo]: valor },
    }));
  };

  const togglePermiso = (id, userId) => {
    const cur = toPermisosArray(editingRows[id]?.permisos ?? "");
    const uid = String(userId);
    const next = cur.includes(uid) ? cur.filter((p) => p !== uid) : [...cur, uid];
    onEditChange(id, "permisos", permisosToCsv(next));
  };

  const guardarFila = async (id) => {
    if (!puedeEditar) return;

    const draft = editingRows[id];
    if (!draft) return;

    const nombre = String(draft.nombre || "").trim();
    if (!nombre) {
      setMensajePorEstanque((prev) => ({
        ...prev,
        [id]: { tipo: "error", texto: "El nombre no puede estar vacío." },
      }));
      return;
    }

    try {
      const payload = {
        nombre,
        descripcion: draft.descripcion ?? "",
        estaActivo: !!draft.estaActivo,
        permisos: toPermisosArray(draft.permisos).map(Number),
      };

      await estanqueService.actualizarEstanque(id, payload);

      // reflejar cambios en la tabla base
      setEstanques((prev) =>
        prev.map((e) =>
          e.id === id ? { ...e, ...payload, permisos: permisosToCsv(payload.permisos) } : e
        )
      );

      setMensajePorEstanque((prev) => ({
        ...prev,
        [id]: { tipo: "exito", texto: "Estanque actualizado correctamente." },
      }));

      // salir de edición
      cancelEdit(id);

      // limpiar mensaje a los 3s
      setTimeout(() => {
        setMensajePorEstanque((prev) => {
          const cp = { ...prev };
          delete cp[id];
          return cp;
        });
      }, 3000);
    } catch (err) {
      console.error(err);
      setMensajePorEstanque((prev) => ({
        ...prev,
        [id]: { tipo: "error", texto: "Error al actualizar el estanque." },
      }));
    }
  };

  if (!puedeVer) {
    return (
      <div className="bg-neutral-950 text-neutral-300 p-6 rounded-lg max-w-4xl mx-auto">
        No tienes permisos para ver Estanques.
      </div>
    );
  }

  const usuariosList = Array.isArray(usuarios) ? usuarios : [];

  return (
    <div className="bg-neutral-900 p-6 rounded-lg shadow-lg w-full max-w-7xl mx-auto text-white">
      <GestionArchivo tipo="estanques" puedeEditar={puedeEditar} onChanged={cargarEstanques} />
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-green-400 text-xl font-semibold">Administrar Estanques</h3>
        {esSuper && (
          <span className="text-xs border border-emerald-500/60 px-2 py-1 rounded text-emerald-300">
            Superadministrador
          </span>
        )}
      </div>

      {errorGlobal && (
        <div className="mb-3 rounded border border-red-500/40 bg-red-500/10 text-red-300 px-3 py-2">
          {errorGlobal}
        </div>
      )}

      <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar estanque por nombre, descripción o estado…"
        className="w-full mb-4 p-2.5 rounded bg-neutral-800 border border-cyan-700/40 text-white" />

      <div className="overflow-x-auto rounded-lg border border-cyan-700/40">
        <table className="min-w-[900px] w-full text-sm">
          <thead className="bg-neutral-800 text-cyan-300">
            <tr>
              <th className="p-2 border border-cyan-700/40 text-left w-[22%]">Nombre</th>
              <th className="p-2 border border-cyan-700/40 text-left w-[32%]">Descripción</th>
              <th className="p-2 border border-cyan-700/40 text-center w-[10%]">Activo</th>
              <th className="p-2 border border-cyan-700/40 text-left w-[26%]">Permisos</th>
              <th className="p-2 border border-cyan-700/40 text-center w-[10%]">Acción</th>
            </tr>
          </thead>

          <tbody>
            {estanquesFiltrados.map((e) => {
              const edit = editingRows[e.id];

              // campos controlados: si hay modo edición uso draft, si no el valor actual
              const nombre = edit ? edit.nombre : (e.nombre ?? "");
              const descripcion = edit ? edit.descripcion : (e.descripcion ?? "");
              const estaActivo = edit ? !!edit.estaActivo : !!e.estaActivo;
              const permisosCsv = edit ? edit.permisos : permisosToCsv(e.permisos ?? "");
              const permisosArr = toPermisosArray(permisosCsv);

              return (
                <tr key={e.id} className="hover:bg-neutral-800 align-top border-t border-cyan-700/30">
                  {/* Nombre */}
                  <td className="p-2 border border-cyan-700/30">
                    <input
                      type="text"
                      value={nombre}
                      onChange={(ev) => onEditChange(e.id, "nombre", ev.target.value)}
                      className={`w-full p-2 rounded bg-neutral-800 border ${
                        edit && puedeEditar
                          ? "border-neutral-500 focus:border-cyan-400 outline-none"
                          : "border-neutral-700"
                      }`}
                      disabled={!edit || !puedeEditar}
                    />
                  </td>

                  {/* Descripción (autosize) */}
                  <td className="p-2 border border-cyan-700/30">
                    <textarea
                      value={descripcion}
                      onChange={(ev) => {
                        onEditChange(e.id, "descripcion", ev.target.value);
                        autosize(ev.target);
                      }}
                      ref={(el) => el && autosize(el)}
                      rows={1}
                      className={`w-full resize-none p-2 rounded bg-neutral-800 border leading-6 ${
                        edit && puedeEditar
                          ? "border-neutral-500 focus:border-cyan-400 outline-none"
                          : "border-neutral-700"
                      }`}
                      disabled={!edit || !puedeEditar}
                      placeholder="Descripción…"
                    />
                  </td>

                  {/* Activo */}
                  <td className="p-2 border border-cyan-700/30 text-center">
                    <select
                      value={estaActivo ? "true" : "false"}
                      onChange={(ev) => onEditChange(e.id, "estaActivo", ev.target.value === "true")}
                      className={`p-2 rounded bg-neutral-800 border ${
                        edit && puedeEditar
                          ? "border-neutral-500 focus:border-cyan-400 outline-none"
                          : "border-neutral-700"
                      }`}
                      disabled={!edit || !puedeEditar}
                    >
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  </td>

                  {/* Permisos (lista de checks) */}
                  <td className="p-2 border border-cyan-700/30 max-w-xs">
                    <div className="max-h-36 overflow-y-auto pr-1">
                      {usuariosList.length === 0 && (
                        <div className="text-neutral-400 text-xs">Sin usuarios disponibles.</div>
                      )}
                      {usuariosList.map((u) => {
                        const checked = permisosArr.includes(String(u.id));
                        return (
                          <label key={u.id} className="block text-white/90 text-xs select-none">
                            <input
                              type="checkbox"
                              className="mr-1"
                              checked={checked}
                              onChange={() => togglePermiso(e.id, u.id)}
                              disabled={!edit || !puedeEditar}
                            />
                            {u.nombre} <span className="text-neutral-400">({u.email})</span>
                          </label>
                        );
                      })}
                    </div>
                  </td>

                  {/* Acciones por fila */}
                  <td className="p-2 border border-cyan-700/30 text-center">
                    <a className="block text-cyan-300 mb-2" href={`/dashboard/admin?tab=Dispositivos&sub=crear&estanqueId=${e.id}`}>Agregar dispositivo</a>
                    {!edit ? (
                      <button
                        onClick={() => puedeEditar && startEdit(e)}
                        className={`px-3 py-1 rounded ${
                          puedeEditar
                            ? "bg-yellow-400 hover:bg-yellow-500 text-black"
                            : "bg-neutral-700 text-neutral-300 cursor-not-allowed"
                        }`}
                        disabled={!puedeEditar}
                      >
                        Editar
                      </button>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => guardarFila(e.id)}
                          className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={!puedeEditar}
                        >
                          Guardar
                        </button>
                        <button
                          onClick={() => cancelEdit(e.id)}
                          className="px-3 py-1 rounded bg-neutral-600 hover:bg-neutral-700 text-white"
                        >
                          Cancelar
                        </button>
                      </div>
                    )}

                    {mensajePorEstanque[e.id] && (
                      <p
                        className={`mt-2 text-xs ${
                          mensajePorEstanque[e.id].tipo === "exito" ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {mensajePorEstanque[e.id].texto}
                      </p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {estanques.length === 0 && !errorGlobal && fetchedOnceRef.current && (
        <p className="text-center text-gray-400 mt-4 text-sm">No hay estanques para mostrar.</p>
      )}
    </div>
  );
};

export default AdministrarEstanques;
